import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useTimeZones } from '../hooks/use-time-zones';
import { timeZoneGroups, timeZoneLabel, timeZoneOffset } from '../schemas/timezones';

export interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
}

/**
 * Searchable IANA zone picker grouped by region. Free text was accepted before and "New York" or
 * "EST5" then broke reports and exports on the server, so only a known zone can be chosen.
 */
export function TimezoneSelect({
  value,
  onChange,
  onBlur,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
}: TimezoneSelectProps) {
  const { t } = useTranslation('stores');
  const [open, setOpen] = useState(false);
  const zones = useTimeZones();
  const groups = useMemo(() => timeZoneGroups(zones), [zones]);
  const offset = value ? timeZoneOffset(value) : null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground')}
        >
          <span className="truncate">
            {value ? (
              <>
                <span className="font-mono text-[13px]">{value}</span>
                {offset ? (
                  <span className="text-muted-foreground ml-2 text-xs">{offset}</span>
                ) : null}
              </>
            ) : (
              t('timezone.placeholder')
            )}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder={t('timezone.search')} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t('timezone.empty')}</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.region} heading={group.region}>
                {group.zones.map((zone) => (
                  <CommandItem
                    key={zone}
                    value={zone}
                    keywords={[timeZoneLabel(zone), group.region]}
                    onSelect={() => {
                      onChange(zone);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('size-4', zone === value ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="flex-1 truncate">{timeZoneLabel(zone)}</span>
                    <span className="text-muted-foreground font-mono text-[11px]">
                      {timeZoneOffset(zone) ?? ''}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
